import React, { useState, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import Moment from "react-moment";
import PaginationNav from "../PaginationNav/PaginationNav";
import SortControls from "../SortControls/SortControls";
import {
  getPaginatedResults,
  orderByDate,
  getAggregatedList,
  checkNullFilter,
} from "../../utils/sortFunctions";
import { normalizeAmtStr } from "../../utils/numNormalizeFunctions";
import { convictions } from "../../utils/convictions";
import { charges } from "../../utils/charges";
import { FiExternalLink } from "react-icons/fi";
import PillSortButton from "../PillInput/PillSortButton";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
import { FaFilter } from "react-icons/fa";
import PillInput from "../PillInput/PillInput";
import AutoCompleteInput from "../AutoCompleteInput/AutoCompleteInput";

const consolidatedList = orderByDate(
  [...charges, ...convictions].map((entry) => {
    entry.sortDate = entry.convictionDate
      ? entry.convictionDate
      : entry.chargeDate;
    return entry;
  }),
  "sortDate"
);

const ConvictionsList = ({ props }) => {
  const isMobile = useMediaQuery({ maxWidth: 990 });
  const uniqueDefendants = [
    "All",
    ...new Set(consolidatedList.map((item) => item.defendant)),
  ];
  const uniqueCharges = [
    "All",
    ...new Set(consolidatedList.map((item) => item.charge).filter(Boolean)),
  ];
  const [selectedDefendant, setSelectedDefendant] = useState(null);
  const [currentPage, setPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({
    dLocation: [],
    charge: "",
  });
  const [searchOrderBy, setSearchOrderBy] = useState("sortDate");
  const [searchOrderDirection, setSearchOrderDirection] = useState("desc");
  const [filteredConvictions, setFilteredConvictions] =
    useState(consolidatedList);
  const [modalOpen, toggleModal] = useState(false);

  const applyFiltersAndSorting = () => {
    const filtered = consolidatedList
      .filter(
        (conviction) =>
          ((searchFilters.dLocation.length === 0 ||
            searchFilters.dLocation.some(
              (filter) =>
                conviction.defendant &&
                conviction.defendant.toLowerCase() === filter.toLowerCase()
            )) &&
            conviction.dLocation &&
            searchFilters.dLocation.indexOf(
              conviction.dLocation.toLowerCase()
            ) > -1) ||
          checkNullFilter(searchFilters.dLocation)
      )
      .filter(
        (conviction) =>
          !searchFilters.charge ||
          conviction.charge?.toLowerCase() === searchFilters.charge.toLowerCase()
      )
      .sort((a, b) => {
        if (searchOrderBy === "defendant") {
          return searchOrderDirection === "asc"
            ? a.defendant.localeCompare(b.defendant)
            : b.defendant.localeCompare(a.defendant);
        } else if (searchOrderBy === "dLocation") {
          return searchOrderDirection === "asc"
            ? a.dLocation.localeCompare(b.dLocation)
            : b.dLocation.localeCompare(a.dLocation);
        } else if (searchOrderBy === "charge") {
          return searchOrderDirection === "asc"
            ? a.charge.localeCompare(b.charge)
            : b.charge.localeCompare(a.charge);
        } else if (searchOrderBy === "sortDate") {
          return searchOrderDirection === "asc"
            ? new Date(a.sortDate) - new Date(b.sortDate)
            : new Date(b.sortDate) - new Date(a.sortDate);
        }
        return 0;
      });
    setFilteredConvictions(filtered);
  };

  const setListOrder = (filterId) => {
    if (filterId === searchOrderBy && searchOrderDirection === "asc") {
      setSearchOrderDirection("desc");
    } else {
      setSearchOrderBy(filterId);
      setSearchOrderDirection("asc");
    }
  };

  const applyFilters = (defendantFilter) => {
    let filtered = consolidatedList;
    if (defendantFilter !== "All") {
      setSelectedDefendant(defendantFilter);
      filtered = filtered.filter((item) => item.defendant === defendantFilter);
    } else {
      setSelectedDefendant(null);
    }
    setFilteredConvictions(filtered);
  };

  useEffect(() => {
    applyFiltersAndSorting();
  }, [searchFilters, searchOrderBy, searchOrderDirection]);

  const dLocations = getAggregatedList(consolidatedList, "dLocation");
  const defendants = getAggregatedList(consolidatedList, "defendant");

  return (
    <>
      <div className="flexContainer mobileCol justifyBetween alignEnd">
        <SortControls>
          <PillSortButton
            id="sortDate"
            label="Date"
            active={searchOrderBy === "sortDate"}
            order={searchOrderDirection}
            clickEvt={() => setListOrder("sortDate")}
          />
          <PillSortButton
            id="defendant"
            label="Defendant"
            active={searchOrderBy === "defendant"}
            order={searchOrderDirection}
            clickEvt={() => setListOrder("defendant")}
          />
          <PillSortButton
            id="dLocation"
            label="Defendant Location"
            active={searchOrderBy === "dLocation"}
            order={searchOrderDirection}
            clickEvt={() => setListOrder("dLocation")}
          />
          <PillSortButton
            id="charge"
            label="Charge"
            active={searchOrderBy === "charge"}
            order={searchOrderDirection}
            clickEvt={() => setListOrder("charge")}
          />
        </SortControls>

        <Button
          id="filterBtn"
          icon={<FaFilter />}
          size="sm"
          clickEvt={() => toggleModal(!modalOpen)}
          text="Search Result"
        />
      </div>

      {modalOpen && (
        <Modal closeEvt={() => toggleModal(!modalOpen)}>
          <div>
            <h2>Filter Options</h2>
            <div>
              <div style={{ marginBottom: `.5rem` }}>
                <span className="bold">Defendant</span>
              </div>
              <AutoCompleteInput
                options={uniqueDefendants}
                selectedOption={selectedDefendant || "All"}
                onChange={(value) => {
                  setSelectedDefendant(value); // Update the selected option state
                  applyFilters(value); // Apply filters based on the selected option
                }}
                value={selectedDefendant || "All"} // Ensure the value reflects the selected option
                placeholder="Select a defendant"
              />
            </div>
            <div style={{ marginTop: `1rem` }}>
              <div style={{ marginBottom: `.5rem` }}>
                <span className="bold">Charge</span>
              </div>
              <AutoCompleteInput
                options={uniqueCharges}
                selectedOption={searchFilters.charge || "All"}
                onChange={(value) => {
                  setSearchFilters((prevFilters) => ({
                    ...prevFilters,
                    charge: value === "All" ? "" : value,
                  }));
                }}
                value={searchFilters.charge || "All"}
                placeholder="Select a charge"
              />
            </div>
          </div>
        </Modal>
      )}

      <div className="overflowContainer">
        {!isMobile ? (
          <table className="noWrap">
            <thead>
              <tr>
                <th>Charge Date</th>
                <th>Defendant</th>
                <th>Defendant Location</th>
                <th>Offence Location</th>
                <th>Charge</th>
                <th>Conviction Date</th>
                <th>Conviction Result</th>
                <th>Fine/Surcharge Amount</th>
                <th>Under Appeal</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedResults(filteredConvictions, currentPage).map(
                (row) => {
                  return (
                    <tr>
                      <td>
                        {row["chargeDate"] ? (
                          <Moment format="MMMM D, YYYY">
                            {row["chargeDate"]}
                          </Moment>
                        ) : (
                          <span>---</span>
                        )}
                      </td>
                      <td>
                        <a href={row["link"]} className="linkHeader">
                          <FiExternalLink
                            style={{
                              verticalAlign: "center",
                              marginRight: ".5rem",
                              fontSize: `1.5rem`,
                            }}
                          />
                          {row["defendant"] ? row["defendant"] : "---"}
                        </a>
                      </td>
                      <td>{row["dLocation"] ? row["dLocation"] : "---"}</td>
                      <td>{row["oLocation"] ? row["oLocation"] : "---"}</td>
                      <td>{row["charge"] ? row["charge"] : "---"}</td>
                      <td>
                        {row["convictionDate"] ? (
                          <Moment format="MMMM D, YYYY">
                            {row["convictionDate"]}
                          </Moment>
                        ) : (
                          <span>---</span>
                        )}
                      </td>
                      <td>{row["result"] ? row["result"] : "---"}</td>
                      <td>
                        {row["fineAmount"] ? row["fineAmount"] : `---`}
                      </td>
                      <td>
                        {row["underAppeal"] && row["underAppeal"] !== "NULL"
                          ? row["underAppeal"]
                          : `---`}
                      </td>
                    </tr>
                  );
                }
              )}
            </tbody>
          </table>
        ) : (
          <>
            {getPaginatedResults(filteredConvictions, currentPage).map(
              (row) => {
                return (
                  <div className="tile">
                    <div>
                      <span className="label">Charge Date: </span>
                      <span>
                        {row["chargeDate"] ? (
                          <Moment format="MMMM D, YYYY">
                            {row["chargeDate"]}
                          </Moment>
                        ) : (
                          <span>---</span>
                        )}
                      </span>
                    </div>

                    <div>
                      <span className="label">Defendant: </span>
                      <span>
                        {row["defendant"] ? row["defendant"] : "---"}
                      </span>
                    </div>

                    <div>
                      <span className="label">Defendant Location: </span>
                      <span>
                        {row["dLocation"] ? row["dLocation"] : "---"}
                      </span>
                    </div>

                    <div>
                      <span className="label">Offence Location: </span>
                      <span>
                        {row["oLocation"] ? row["oLocation"] : "---"}
                      </span>
                    </div>

                    <div>
                      <span className="label">Charge: </span>
                      <span>{row["charge"] !== "" ? row["charge"] : "---"}</span>
                    </div>

                    <div>
                      <span className="label">Conviction Date: </span>
                      <span>
                        {row["convictionDate"] ? (
                          <Moment format="MMMM D, YYYY">
                            {row["convictionDate"]}
                          </Moment>
                        ) : (
                          <span>---</span>
                        )}
                      </span>
                    </div>

                    <div>
                      <span className="label">Conviction Result: </span>
                      <span>
                        {row["result"]
                          ? row["result"]
                          : "---"}
                      </span>
                    </div>

                    <div>
                      <span className="label">Fine Amount: </span>
                      <span>
                        {row["fineAmount"] ? row["fineAmount"] : `---`}
                      </span>
                    </div>

                    <div>
                      <span className="label">Under Appeal: </span>
                      <span>
                        {row["underAppeal"] && row["underAppeal"] !== "NULL"
                          ? row["underAppeal"]
                          : `---`}
                      </span>
                    </div>
                  </div>
                );
              }
            )}
          </>
        )}
      </div>

      {filteredConvictions.length > 10 && (
        <PaginationNav
          currentPage={currentPage}
          clickEvt={setPage}
          resultsLength={filteredConvictions.length}
        />
      )}
    </>
  );
};

export default ConvictionsList;
